import { useState, useEffect, useMemo, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { MobileNavProvider } from './MobileNavContext';
import TrialStatusBanner from '../billing/TrialStatusBanner';
import ImpersonationBanner from '../common/ImpersonationBanner';
import FeedbackWidget from '../common/FeedbackWidget';
import UserGuideTour from '../UserGuideTour';
import TodayFollowUpReminder from '@/components/today/TodayFollowUpReminder';
import { ProjectProvider, useProjectContext } from '@/context/ProjectContext';
import { PlatformProvider } from '@/context/PlatformContext';
import { storage } from '@/utils/storage';
import { useAuth } from '@/hooks/useAuth';
import { isPlatformOperator } from '@/utils/platformOperator';
import { ORG_SETUP_PATH } from '@/utils/orgConsole';
import { isAllowedWithoutProductLicense, isDeliveryUserRole, userHasProductLicense } from '@/utils/productLicense';
import { shouldUseOrgConsoleNav } from '@/utils/orgConsole';
import OrgAdminSetupGuard from '@/components/org/OrgAdminSetupGuard';
import ProductLicenseGuard from '@/components/auth/ProductLicenseGuard';
import ProductAccessRoutingGuard from '@/components/auth/ProductAccessRoutingGuard';
import { useGetMyProductLicensesQuery } from '@/store/api/apiSlice';
import type { ProductId } from '@/config/products';
import {
  DEFAULT_SIDEBAR_WIDTH,
  clampSidebarWidth,
} from '@/utils/sidebarLayout';

// Remounts the active page whenever the global project selection changes,
// so all local state (filters, selections, modals) resets to the new project.
function ProjectKeyedOutlet() {
  const { selectedProjectId } = useProjectContext();
  return <Outlet key={selectedProjectId ?? 'no-project'} />;
}

export default function Layout() {
  const { user } = useAuth();
  const platformMode = isPlatformOperator(user?.role);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const [runTour, setRunTour] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => storage.getSidebarCollapsed());
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = storage.getSidebarWidth();
    return clampSidebarWidth(saved ?? DEFAULT_SIDEBAR_WIDTH);
  });

  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  useEffect(() => {
    window.addEventListener('cotaskr:mobile-nav-open', openMobileNav);
    window.addEventListener('cotaskr:mobile-nav-close', closeMobileNav);
    (window as Window & { __cotaskrMobileNav?: { open: () => void; close: () => void } }).__cotaskrMobileNav = {
      open: openMobileNav,
      close: closeMobileNav,
    };
    return () => {
      window.removeEventListener('cotaskr:mobile-nav-open', openMobileNav);
      window.removeEventListener('cotaskr:mobile-nav-close', closeMobileNav);
      delete (window as Window & { __cotaskrMobileNav?: unknown }).__cotaskrMobileNav;
    };
  }, [openMobileNav, closeMobileNav]);

  const mobileNavApi = useMemo(
    () => ({ open: openMobileNav, close: closeMobileNav }),
    [openMobileNav, closeMobileNav],
  );

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    storage.setSidebarCollapsed(sidebarCollapsed);
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!sidebarCollapsed) {
      storage.setSidebarWidth(sidebarWidth);
    }
  }, [sidebarWidth, sidebarCollapsed]);

  const isOrgSetupPage =
    location.pathname === ORG_SETUP_PATH ||
    location.pathname.startsWith(`${ORG_SETUP_PATH}/`);

  const isDeliveryUser = isDeliveryUserRole(user?.role);
  const orgConsoleMode = shouldUseOrgConsoleNav(user?.role, location.pathname);
  const { data: myLicenses, isLoading: licensesLoading, isError: licensesError } =
    useGetMyProductLicensesQuery(undefined, {
      skip: !user || platformMode,
    });
  // Soft-fail: on API error treat as unknown — do not hide Plan chrome / trap awaiting-access.
  const launchable = licensesError
    ? null
    : ((myLicenses?.launchableProductIds ?? []) as ProductId[]);
  const hasPlanLicense = launchable != null && userHasProductLicense(launchable, 'pm');
  const deliveryUserWithoutLicenses =
    isDeliveryUser &&
    !licensesLoading &&
    !licensesError &&
    Array.isArray(launchable) &&
    launchable.length === 0;
  const useMinimalShell =
    deliveryUserWithoutLicenses && isAllowedWithoutProductLicense(location.pathname);
  const hidePlanChrome =
    !platformMode &&
    !orgConsoleMode &&
    !licensesLoading &&
    !licensesError &&
    launchable != null &&
    !hasPlanLicense &&
    launchable.length > 0;

  if (isOrgSetupPage || useMinimalShell) {
    const minimalMain = (
      <div className="h-screen overflow-hidden bg-bg">
        <main className="h-full overflow-y-auto">
          <Outlet />
        </main>
      </div>
    );

    return (
      <ProjectProvider>
        <ProductAccessRoutingGuard>
          {isOrgSetupPage ? (
            <OrgAdminSetupGuard>{minimalMain}</OrgAdminSetupGuard>
          ) : (
            minimalMain
          )}
        </ProductAccessRoutingGuard>
      </ProjectProvider>
    );
  }

  if (hidePlanChrome) {
    return (
      <ProjectProvider>
        <ProductAccessRoutingGuard>
          <OrgAdminSetupGuard>
            <div className="h-screen flex flex-col overflow-hidden bg-bg">
              <Header onStartTour={() => setRunTour(true)} />
              <PlatformProvider>
                <main className="flex-1 overflow-hidden flex flex-col scrollbar-thin p-3">
                  <ProductLicenseGuard>
                    <ProjectKeyedOutlet />
                  </ProductLicenseGuard>
                </main>
              </PlatformProvider>
            </div>
          </OrgAdminSetupGuard>
        </ProductAccessRoutingGuard>
      </ProjectProvider>
    );
  }

  return (
    <ProjectProvider>
    <MobileNavProvider value={mobileNavApi}>
    <ProductAccessRoutingGuard>
    <OrgAdminSetupGuard>
    <div className="h-screen flex overflow-hidden bg-bg relative" data-mobile-nav-open={mobileNavOpen ? 'true' : 'false'}>
      {mobileNavOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          aria-label="Close navigation menu"
          data-mobile-nav-backdrop="true"
          data-testid="mobile-nav-backdrop"
          onClick={closeMobileNav}
        />
      )}
      <Sidebar
        isCollapsed={sidebarCollapsed}
        setIsCollapsed={setSidebarCollapsed}
        width={sidebarWidth}
        onWidthChange={setSidebarWidth}
        mobileOpen={mobileNavOpen}
        onMobileClose={closeMobileNav}
      />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <Header onStartTour={() => setRunTour(true)} />
        <PlatformProvider>
          {!platformMode && <TrialStatusBanner />}
          <ImpersonationBanner />
          <main className="flex-1 overflow-hidden flex flex-col scrollbar-thin p-3">
            <ProductLicenseGuard>
              <ProjectKeyedOutlet />
            </ProductLicenseGuard>
          </main>
        </PlatformProvider>
      </div>

      {!platformMode && <FeedbackWidget />}
      {!platformMode && <TodayFollowUpReminder />}
      {!platformMode && <UserGuideTour run={runTour} onFinish={() => setRunTour(false)} />}
    </div>
    </OrgAdminSetupGuard>
    </ProductAccessRoutingGuard>
    </MobileNavProvider>
    </ProjectProvider>
  );
}
